<template>
  <BaseWidget
    :title="content.title"
    :widgetId="content.id"
    :isWidgetLoading="isWidgetLoading"
    :edit="edit"
    :content="content"
    :getWidgetReport="getWidgetReport"
  >
    <template v-slot:widget-control>
      <div v-for="item in preparedControls" v-bind:key="item.name">
        <ConfigurationInput
          :setting="item"
          :key="item.name"
          :settingName="item.name"
          :chartSetting="content"
          :disabled="edit"
          :param="currentControls"
          @input="onControlsUpdate"
          :module="EXPLORE_MODULE"
        />
      </div>
    </template>
    <div class="chart-widget__container">
      <WidgetNotification v-show="showError" :message="errorMessage" />
      <BaseChart
        v-if="Object.keys(currentControls).length"
        v-show="!showError"
        :chartType="content.chartType"
        :chart="preparedContent"
        @widgetLoading="onLoading"
        @updateChartObject="updateChartObject"
        v-on="$listeners"
      />
    </div>
  </BaseWidget>
</template>

<script lang="ts">
import { cloneDeep, isEqual, pickBy } from "lodash";
import Vue from "vue";
import { mapGetters, mapMutations } from "vuex";

import BaseChartClass from "@/classes/BaseChart";
import BaseChart from "@/components/charts/BaseChart.vue";
import BaseWidget from "@/components/widgets/explore/BaseWidget.vue";
import { EXPLORE_MODULE } from "@/constants";
import csvExport, { buildFileName } from "@/helpers/csvExport";
import Lang from "@/lang";
import chartComponentsConfig from "@/settings/chartComponentConfigs";
import { chartsSettings, getChartAllControls } from "@/settings/charts";
import {
  DIMENSION_CONTROL_NAME,
  GRANULARITY_CONTROL_NAME,
  INVERSE_CONTROL_NAME,
  PRIMARY_METRIC_CONTROL_NAME,
} from "@/settings/charts/settingsDictionary";
import inputsDictionary from "@/settings/inputsDictionary";
import { getTimeAxisName } from "@/settings/widgets";
import { ContentWidget } from "@/types/layouts";

const WidgetNotification = () => import("@/components/WidgetNotification.vue");
const ConfigurationInput = () => import("@/components/customComponents/ConfigurationInput.vue");

interface ChartWidgetData {
  isWidgetLoading: boolean;
  Lang: Record<string, unknown>;
  currentControls: Record<string, unknown>;
  currentSettings: Record<string, unknown>;
  chartComponentExist: boolean;
  chartObject: BaseChartClass;
  module: string;
}

export default Vue.extend({
  name: "ChartWidget",

  props: {
    content: Object as () => ContentWidget,
    edit: Boolean,
  },

  components: {
    BaseChart,
    ConfigurationInput,
    WidgetNotification,
    BaseWidget,
  },

  created() {
    this.chartComponentExist = !!chartComponentsConfig[this.content?.chartType];
  },

  beforeMount() {
    this.currentSettings = this.content?.settings;
  },

  mounted() {
    this.setCurrentControls();
  },

  data() {
    return {
      isWidgetLoading: false,
      currentControls: {},
      currentSettings: {},
      chartComponentExist: false,
      Lang,
      EXPLORE_MODULE,
    } as unknown as ChartWidgetData;
  },

  watch: {
    "content.chartType": {
      handler(): void {
        this.currentControls = {
          [INVERSE_CONTROL_NAME]: false,
          [GRANULARITY_CONTROL_NAME]: this.content?.settings[this.timeAxis],
        };
      },
    },

    "content.settings": {
      handler(value): void {
        this.currentControls = {
          ...this.currentControls,
          [GRANULARITY_CONTROL_NAME]: this.content?.settings[this.timeAxis],
        };
        this.currentSettings = value;
      },
    },
  },

  computed: {
    ...mapGetters("userSettings", ["filterParams", "dashboards"]),
    ...mapGetters("charts", ["noDataStatuses"]),

    preparedControls(): Record<string, string | number | boolean | object>[] {
      const { chartType, family } = this.content || {};
      let chartControls = getChartAllControls(chartType, family) || [];

      if (!this.content?.settings?.dimensionSelector) {
        chartControls = chartControls.filter((item) => item.name !== DIMENSION_CONTROL_NAME);
      }

      if (!this.content?.settings?.primaryMetricSelector) {
        chartControls = chartControls.filter((item) => item.name !== PRIMARY_METRIC_CONTROL_NAME);
      }

      return chartControls;
    },

    currentCleanedControls(): Record<string, unknown> {
      return pickBy(this.currentControls, (value, key) => this.preparedControls.find((item) => item.name === key));
    },

    preparedContent(): ContentWidget {
      return {
        ...this.content,
        settings: cloneDeep(this.currentSettings),
        controls: cloneDeep(this.currentCleanedControls),
      };
    },

    dashboardName(): string {
      return this.$route.params.dashboardName;
    },

    timeAxis(): string {
      return getTimeAxisName(this.content.settings);
    },

    showError(): boolean {
      return !this.chartComponentExist || this.noDataStatuses[this.content.id];
    },

    errorMessage(): string {
      return !this.chartComponentExist ? Lang.noChart : Lang.noData;
    },

    primaryMetricName(): string | null {
      const primaryControl = this.preparedControls.find((control) => control.name === PRIMARY_METRIC_CONTROL_NAME);

      if (primaryControl) {
        return primaryControl.metricName as string;
      }

      return null;
    },
  },

  methods: {
    ...mapMutations("pageEdit", ["updateSelectWidget"]),

    hasControl(control: string): boolean {
      return control in (this.preparedContent?.controls || {});
    },

    onLoading(val: boolean) {
      this.isWidgetLoading = val;
    },

    updateChartObject(val: BaseChartClass): void {
      this.chartObject = val;
    },

    getWidgetReport(isSingleChart = true) {
      csvExport(this.chartObject.exportData(isSingleChart), buildFileName(this.content?.title));
    },

    setCurrentControls(): void {
      this.currentControls = {
        [INVERSE_CONTROL_NAME]: false,
        [GRANULARITY_CONTROL_NAME]: this.content?.settings[this.timeAxis],
        [DIMENSION_CONTROL_NAME]: this.content?.settings[this.getDimensionMetricName()],
        [PRIMARY_METRIC_CONTROL_NAME]: this.content?.settings?.[this.primaryMetricName as string],
      };
    },

    getDimensionMetricName(): string {
      const controls = chartsSettings[this.preparedContent.chartType]?.controls || [];
      const dimensionSelector = controls.find((item) => item.name === DIMENSION_CONTROL_NAME);

      return dimensionSelector ? (dimensionSelector.metricName as string) : "decompositionMetric";
    },

    onControlsUpdate(newControlsValue: Record<string, unknown>): void {
      const swapSettings = (paramSettings: Record<string, unknown>, firstKey: string, secondKey: string) => {
        const settings = cloneDeep(paramSettings);

        if (paramSettings[firstKey] && paramSettings[secondKey]) {
          settings[firstKey] = paramSettings[secondKey];
          settings[secondKey] = paramSettings[firstKey];
        }

        return settings;
      };

      const hasInverseControl = this.hasControl(INVERSE_CONTROL_NAME);
      const hasGranularityControl = this.hasControl(GRANULARITY_CONTROL_NAME);
      const hasDimensionControl = this.hasControl(DIMENSION_CONTROL_NAME);
      const hasPrimaryMetricControl = this.hasControl(PRIMARY_METRIC_CONTROL_NAME);

      const newControls = { ...this.currentControls, ...newControlsValue };
      let newSettings: Record<string, unknown> = cloneDeep(this.currentSettings);

      const dimensionMetricName = this.getDimensionMetricName();

      const isInverseControlChanged =
        this.currentControls?.[INVERSE_CONTROL_NAME] !== newControlsValue?.[INVERSE_CONTROL_NAME] && hasInverseControl;

      if (hasGranularityControl) {
        newSettings[this.timeAxis] = newControls?.[GRANULARITY_CONTROL_NAME];
      }

      if (isInverseControlChanged) {
        newSettings = swapSettings(newSettings, dimensionMetricName, "decompositionMetricSecondary");
        newSettings = swapSettings(newSettings, "yAxisName", "yAxisSecondaryName");
        newSettings = swapSettings(newSettings, "xAxisName", "xAxisSecondaryName");
      }

      if (hasDimensionControl) {
        if (isInverseControlChanged) {
          newControls[DIMENSION_CONTROL_NAME] = newSettings[dimensionMetricName];
        } else {
          newSettings[dimensionMetricName] = newControls[DIMENSION_CONTROL_NAME];

          const settings = chartsSettings[this.preparedContent.chartType].settings;
          const decompositionMetricTitleKey = Object.keys(settings).find(
            (key: string) => settings[key]?.forAxis === dimensionMetricName
          );

          if (decompositionMetricTitleKey) {
            newSettings[decompositionMetricTitleKey] =
              inputsDictionary.dimensionMetrics.find((el) => {
                return el.value === newControls[DIMENSION_CONTROL_NAME];
              })?.title || "";
          }
        }
      }

      if (hasPrimaryMetricControl) {
        newSettings[this.primaryMetricName as string] = newControls?.[PRIMARY_METRIC_CONTROL_NAME];

        const settings = chartsSettings[this.preparedContent.chartType].settings;
        const metricTitleKey = Object.keys(settings).find(
          (key: string) => settings[key]?.forAxis === this.primaryMetricName
        );

        if (metricTitleKey) {
          newSettings[metricTitleKey] =
            inputsDictionary.displayMetrics.find((el) => {
              return el.value === newControls[PRIMARY_METRIC_CONTROL_NAME];
            })?.title || "";
        }
      }

      if (!isEqual(this.currentSettings, newSettings)) {
        this.currentSettings = newSettings;
      }

      if (!isEqual(this.currentControls, newControls)) {
        this.currentControls = newControls;
      }
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/scss/theme.scss";
@import "@/scss/custom_variables.scss";

.chart-widget {
  &__container {
    position: relative;
    flex-grow: 1;
  }
}
</style>
